import { useContext, useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { Target } from '../../../../client';
import { GRAssetsBySkinId } from '../../constants/assets';
import { GRContext } from '../../contexts/GRContext';
import { GRSkinIds } from '../../types/global';

const appearAnim = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

type StyledButtonProps = {
  $backgroundPath: string;
  $disabled?: boolean;
};

const StyledButton = styled.div<StyledButtonProps>`
  white-space: nowrap;
  font-size: 135%;
  padding: 1em;

  transition: none 150ms ease-in-out;
  transition-property: transform, filter;
  background-image: ${({ $backgroundPath }) => `url(${$backgroundPath})`};
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center center;

  user-select: none;

  &.gr-restart-round-button-appearance-transition-enter {
    animation: ${appearAnim} 300ms ease-in-out forwards;
  }
  &.gr-restart-round-button-appearance-transition-exit {
    animation: ${appearAnim} 300ms ease-in-out forwards reverse;
  }

  ${({ $disabled }) => css`
    ${$disabled
      ? css`
          filter: grayscale(1);
          pointer-events: none;
        `
      : css`
          cursor: pointer;

          &:active {
            transform: scale(0.95);
          }

          ${!Target.__MOBILE__
            ? css`
                &:hover {
                  filter: brightness(110%);
                }
              `
            : undefined}
        `}
  `}
`;

type GRButtonProps = {
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  isSecondary?: boolean;
};

const GRButton = ({ className, children, onClick, disabled, isSecondary = false }: GRButtonProps) => {
  const { skinId } = useContext(GRContext);

  const skinBackgroundPath = useMemo(() => {
    const buttonType = isSecondary ? 'secondary' : 'primary';

    if (!skinId) return GRAssetsBySkinId[GRSkinIds.GIFT_RACE].Buttons[buttonType] || '';

    return GRAssetsBySkinId[skinId]?.Buttons[buttonType] || '';
  }, [isSecondary, skinId]);

  return (
    <StyledButton className={className} onClick={onClick} $disabled={disabled} $backgroundPath={skinBackgroundPath}>
      {children}
    </StyledButton>
  );
};

export default GRButton;
